import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { DescriptionTooltip } from '@/shared/components/description-tooltip'
import { Badge } from '@/shared/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * The card every widget is drawn in: its name, its description behind an info
 * icon, and whatever it has to show.
 *
 * Shared rather than repeated per widget type, because the header is the part a
 * reader scans across a board and it has to be the same shape in every card.
 * What differs between a summary and a chart is only what fills the body.
 */
export function WidgetFrame({
  widgetId,
  title,
  description,
  misconfigured = false,
  loading = false,
  actions,
  children,
}: {
  widgetId: number
  title: string
  description?: string
  /**
   * Whether the data source behind this widget refused to answer.
   *
   * Shown as a badge in the header rather than replacing the body, so a widget
   * that has been broken by somebody deleting the field it counted still says
   * what it was for.
   */
  misconfigured?: boolean
  loading?: boolean
  /**
   * What can be done to this widget, shown at the end of its header.
   *
   * Passed in rather than built here, because the frame is drawn by the widget
   * types and the menu belongs to the board -- it is what knows how to open a
   * widget's settings and what to do with the address bar when it does.
   */
  actions?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()

  return (
    <Card className="gap-3 on-surface" data-testid={`widget-${widgetId}`}>
      <CardHeader className="flex items-start gap-2">
        <CardTitle className="flex min-w-0 flex-1 items-center gap-2 text-base">
          <span className="truncate" data-testid="widget-title">
            {title}
          </span>
          {description !== undefined && <DescriptionTooltip description={description} />}
          {misconfigured && (
            <Badge variant="destructive" className="shrink-0">
              {t('widget.fixConfiguration')}
            </Badge>
          )}
        </CardTitle>
        {actions}
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-16 w-full" /> : children}
      </CardContent>
    </Card>
  )
}
